import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SuperUser from "../../api/SuperUser";
import { toast } from "react-hot-toast";
import Input from "../../components/form/Input";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/organisms/Layout";

const EditPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);

  useEffect(() => {
    document.title = "HeyHire Portal-Edit Password";
  }, []);

  const validatePassword = () => {
    const errors = {};

    // Check password length
    if (!password || password.length < 8) {
      errors.password = ["Password must be at least 8 characters long"];
    }

    // Check password complexity
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = ["Password must contain at least one uppercase letter, one lowercase letter, and one number"];
    }

    // Check password confirmation
    if (!confirmPassword || confirmPassword !== password) {
      errors.confirmPassword = ["Passwords do not match"];
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }

    setValidationErrors(null);
    return true;
  };

  const updateUser = async () => {
    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    const requestBody = {
      user_id: params?.userId,
      password: password,
    };

    SuperUser.changePassword(requestBody)
      .then(() => {
        toast.success(t("general-settings.pssword-updated"));
        navigate(-1);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <DashboardLayout >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/manage-business-users"
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <i className="far fa-chevron-left text-gray-500" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("general-settings.edit-password")}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Update the user's password with a new secure password
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex flex-col items-center">
                <i className="fad fa-circle-notch fa-spin text-3xl text-primary-500" />
                <span className="mt-2 text-sm text-gray-600 font-medium">
                  Updating password...
                </span>
              </div>
            </div>
          )}
          <div className="p-6">
            <form className="max-w-xl space-y-6">
              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Password Requirements:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center">
                    <svg className={`h-4 w-4 mr-2 ${password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    At least 8 characters long
                  </li>
                  <li className="flex items-center">
                    <svg className={`h-4 w-4 mr-2 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    One uppercase letter
                  </li>
                  <li className="flex items-center">
                    <svg className={`h-4 w-4 mr-2 ${/[a-z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    One lowercase letter
                  </li>
                  <li className="flex items-center">
                    <svg className={`h-4 w-4 mr-2 ${/\d/.test(password) ? 'text-green-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    One number
                  </li>
                </ul>
              </div>

              {/* Password Fields */}
              <div className="space-y-4">
                <Input
                  name="password"
                  label={t("common.password")}
                  type="password"
                  value={password}
                  error={validationErrors?.password?.[0]}
                  onChange={(e) => {
                    setValidationErrors(prev => ({...prev, password: null}));
                    setPassword(e.target.value);
                  }}
                  className="w-full rounded-lg"
                  
                />

                <Input
                  name="confirmPassword"
                  label={"Confirm " + t("common.password")}
                  type="password"
                  value={confirmPassword}
                  error={validationErrors?.confirmPassword?.[0]}
                  onChange={(e) => {
                    setValidationErrors(prev => ({...prev, confirmPassword: null}));
                    setConfirmPassword(e.target.value);
                  }}
                  className="w-full rounded-lg"
                  
                />
              </div>
            </form>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <Link
                to="/admin/manage-business-users"
                className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {t("common.cancel")}
              </Link>
              <button
                className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
                onClick={(e) => {
                  e.preventDefault();
                  updateUser();
                }}
                disabled={loading}
              >
                {loading && <i className="fad fa-circle-notch fa-spin mr-2" />}
                {t("general-settings.update-password")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EditPasswordPage;
